const SHOP_ALL_ALLOWLIST = [
  'template-collection-shop-all',
  'template-collection-shop-all-test',
  'template-collection-shop-all-google',
];

const SHOP_ALL_SELECTOR = '.shop-all-placeholder';
const PLP_GRID_SELECTOR = '.product-grid[data-lazy-grid="true"]';
let currentGridLoader = null;

const ready = (callback) => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
  } else {
    callback();
  }
};

class SectionReplaceLoader {
  constructor(options = {}) {
    const configBatchSize = Number(options.maxBatchSize ?? window.ShopAllLoaderConfig?.maxBatchSize);
    const fallbackBatchSize = Number.isFinite(configBatchSize) ? configBatchSize : 4;
    this.maxBatchSize = Math.min(Math.max(1, fallbackBatchSize), 4);
    this.retryAttempts = 2;
    this.retryDelay = 1200;
    this.primeThreshold = 600;
  }

  init() {
    if (!this.shouldActivate()) return;
    this.placeholders = Array.from(document.querySelectorAll(SHOP_ALL_SELECTOR));
    if (!this.placeholders.length) return;

    this.placeholders.forEach((el) => {
      const h = parseInt(el.dataset.height || '0', 10);
      if (h > 0) el.style.minHeight = `${h}px`;
    });

    this.chunk(this.placeholders, this.maxBatchSize).forEach((batch, index) => {
      this.observeBatch(batch, index);
      this.maybePrimeBatch(batch, index);
    });

    document.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('[data-shop-all-retry]');
        if (button) window.location.reload();
      },
      { capture: true }
    );
  }

  shouldActivate() {
    if (window.Shopify?.designMode) return false;
    if (SHOP_ALL_ALLOWLIST.some((cls) => document.body.classList.contains(cls))) return true;

    const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
    return window.location.pathname === `${root}/collections/all`;
  }

  chunk(arr, size) {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  }

  observeBatch(batch, batchIndex) {
    const first = batch[0];
    if (!first) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            io.disconnect();
            this.loadBatch(batch, batchIndex);
          }
        });
      },
      { rootMargin: this.rootMargin(), threshold: 0.01 }
    );
    io.observe(first);
  }

  maybePrimeBatch(batch, batchIndex) {
    const first = batch[0];
    if (!first) return;
    const rect = first.getBoundingClientRect();
    const distanceFromViewport = rect.top - window.innerHeight;
    if (distanceFromViewport <= this.primeDistance()) {
      this.loadBatch(batch, batchIndex);
    }
  }

  primeDistance() {
    return Math.max(window.innerHeight * 0.5, this.primeThreshold);
  }

  rootMargin() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const format = (value) => `${value}px 0px ${value}px 0px`;
    if (connection?.saveData) return format(200);
    if (connection?.effectiveType === '2g') return format(300);
    if (connection?.effectiveType === '3g') return format(600);
    const dynamic = Math.max(Math.round(window.innerHeight * 1.5), 1000);
    return format(dynamic);
  }

  async loadBatch(batch, batchIndex, attempt = 0) {
    const first = batch[0];
    if (!first) return;
    const currentState = this.getBatchState(batch);
    if (attempt === 0) {
      if (currentState === 'loading' || currentState === 'loaded') return;
      this.setBatchState(batch, 'loading');
    }

    const ids = batch.map((el) => el.dataset.renderId).filter(Boolean);
    if (!ids.length) return;

    const url = new URL(window.location.href);
    url.searchParams.set('sections', ids.join(','));

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch(url.toString(), { signal: controller.signal });
      clearTimeout(timeout);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json();

      const insertedSections = ids
        .map((id) => this.swapInSection(id, payload[id]))
        .filter(Boolean);

      insertedSections.forEach((section) => {
        section.querySelectorAll('slider-component').forEach((el) => el.resetPages?.());
      });

      batch.forEach((ph) => ph.querySelector('.loading-indicator')?.setAttribute('aria-live', 'off'));
      this.setBatchState(batch, 'loaded');
      window.dispatchEvent(new CustomEvent('shopAllBatch:success', { detail: { batchIndex, ids } }));
    } catch (error) {
      clearTimeout(timeout);
      if (attempt < this.retryAttempts) {
        setTimeout(() => this.loadBatch(batch, batchIndex, attempt + 1), this.retryDelay * Math.pow(2, attempt));
      } else {
        this.setBatchState(batch, 'error');
        this.handleBatchError(batch, batchIndex, error);
      }
    }
  }

  swapInSection(id, html) {
    if (!html) return;
    const placeholder = document.querySelector(`${SHOP_ALL_SELECTOR}[data-render-id="${id}"]`);
    if (!placeholder) return;

    const container = document.createElement('div');
    container.innerHTML = html;
    this.dedupeReturnedAssets(container);

    const newSection = container.querySelector('.shopify-section');
    const outerWrapper =
      placeholder.closest('[id^="shopify-section-"]') || placeholder.closest('.shopify-section');
    if (outerWrapper && newSection) {
      outerWrapper.replaceWith(newSection);
      return newSection;
    }
    return null;
  }

  dedupeReturnedAssets(container) {
    container.querySelectorAll('link[rel="stylesheet"], script[src]').forEach((node) => node.remove());
  }

  setBatchState(batch, state) {
    batch.forEach((el) => {
      if (state) {
        el.dataset.shopAllState = state;
      } else {
        delete el.dataset.shopAllState;
      }
    });
  }

  getBatchState(batch) {
    return batch[0]?.dataset.shopAllState;
  }

  handleBatchError(batch, batchIndex, error) {
    window.dispatchEvent(
      new CustomEvent('shopAllBatch:error', {
        detail: { batchIndex, ids: batch.map((el) => el.dataset.renderId), error: error?.message },
      })
    );
    batch.forEach((placeholder) => {
      const shell = placeholder.querySelector('.lazy-batch-placeholder');
      if (!shell) return;
      shell.innerHTML = `
        <div class="lazy-batch-error" role="status">
          <p>Unable to load this section.</p>
          <button class="button" data-shop-all-retry>Refresh Page</button>
        </div>`;
    });
  }
}

class CollectionInfiniteScroll {
  constructor(config = {}) {
    this.config = config;
    this.sectionId = config.sectionId;
    this.currentPage = Number(config.initialPage) || 1;
    this.totalPages = Number(config.totalPages) || 1;
    this.pageSize = Number(config.pageSize) || 0;
    this.skeletonCount = Number(config.skeletonCount) || Math.max(4, Math.min(this.pageSize || 8, 8));
    this.grid = null;
    this.sentinel = null;
    this.skeletonTemplate = null;
    this.paginationFallback = null;
    this.observer = null;
    this.loading = false;
    this.pendingPage = null;
    this.skeletonNodes = [];
    this.boundIntersect = this.handleIntersect.bind(this);
    this.messages = {
      loading: 'Loading more products…',
      error: 'Something went wrong. Tap to retry.',
      complete: 'All products loaded.',
      retry: 'Retry',
    };
    this.lazyEnabled = config.lazyEnabled !== false;
    const parsedBias = Number(config.triggerBias);
    this.triggerBias = Number.isFinite(parsedBias) ? parsedBias : 50;
    this.userHasScrolled = typeof window !== 'undefined' ? window.scrollY > 0 : false;
    this.scrollHandler = null;
  }

  init() {
    if (!this.lazyEnabled) {
      return;
    }
    if (!this.sectionId) {
      return;
    }
    this.grid = document.querySelector(`[data-section-id="${this.sectionId}"][data-lazy-grid="true"]`) ||
      document.querySelector(PLP_GRID_SELECTOR);
    if (!this.grid) {
      return;
    }

    this.sentinel = document.querySelector(`[data-plp-sentinel="${this.sectionId}"]`);
    this.skeletonTemplate = document.getElementById(`CollectionSkeletonTemplate-${this.sectionId}`);
    this.paginationFallback = document.querySelector(`[data-pagination-fallback="${this.sectionId}"]`);

    if (!this.sentinel || this.currentPage >= this.totalPages) {
      this.teardownSentinel();
      return;
    }

    this.messages = {
      loading: this.sentinel.dataset.loadingText || 'Loading more products…',
      error: this.sentinel.dataset.errorText || 'Something went wrong. Tap to retry.',
      complete: this.sentinel.dataset.completeText || 'All products loaded.',
      retry: this.sentinel.dataset.retryText || 'Retry',
    };

    if (this.paginationFallback) {
      this.paginationFallback.hidden = true;
    }

    this.sentinel.hidden = false;
    this.setSentinelState('idle');
    this.bindScrollWatcher();
    this.attachObserver();
  }

  attachObserver() {
    if (this.observer) this.observer.disconnect();
    this.observer = new IntersectionObserver(this.boundIntersect, {
      rootMargin: this.rootMargin(),
      threshold: 0.1,
    });
    if (this.sentinel) {
      this.observer.observe(this.sentinel);
      this.maybePrimeInitialBatch();
    }
  }

  baseRootMarginValue() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection?.saveData) return 50;
    if (connection?.effectiveType === '2g') return 75;
    if (connection?.effectiveType === '3g') return 120;
    return 200;
  }

  rootMargin() {
    const format = (value) => `${value}px 0px ${value}px 0px`;
    const biasValue = Number.isFinite(this.triggerBias) ? this.triggerBias : 50;
    const clampedBias = Math.max(0, Math.min(100, biasValue)) / 100; // 0 (early) → 1 (late)

    const viewport = Math.max(window.innerHeight || 0, 600);
    const earlyMargin = Math.max(this.baseRootMarginValue() * 4, viewport * 1.5, 900);
    const lateMargin = Math.min(-0.25 * viewport, -150);
    const interpolated = earlyMargin - clampedBias * (earlyMargin - lateMargin);

    return format(Math.round(interpolated));
  }

  bindScrollWatcher() {
    if (this.userHasScrolled || typeof window === 'undefined') return;
    this.scrollHandler = () => {
      if (window.scrollY > 0) {
        this.userHasScrolled = true;
        window.removeEventListener('scroll', this.scrollHandler);
        this.scrollHandler = null;
      }
    };
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting && entry.target === this.sentinel) {
        this.loadNextPage();
      }
    });
  }

  loadNextPage() {
    if (this.loading) return;
    const nextPage = this.currentPage + 1;
    if (nextPage > this.totalPages) {
      this.complete();
      return;
    }
    this.requestPage(nextPage);
  }

  async requestPage(pageNumber) {
    if (this.loading) {
      return;
    }
    this.loading = true;
    this.pendingPage = pageNumber;
    this.setSentinelState('loading');
    this.injectSkeletons();

    try {
      const html = await this.fetchNextPage(pageNumber);
      const appended = this.appendItems(html);
      this.currentPage = pageNumber;
      this.pendingPage = null;
      this.removeSkeletons();
      this.setSentinelState(this.currentPage >= this.totalPages ? 'complete' : 'idle');
      if (appended > 0) {
        this.emitAppendEvent(appended, pageNumber);
      }
      if (this.currentPage >= this.totalPages || appended === 0) {
        this.complete();
      }
    } catch (error) {
      console.warn('PLP infinite scroll failed', error);
      this.removeSkeletons();
      this.setSentinelState('error');
    } finally {
      this.loading = false;
    }
  }

  async fetchNextPage(pageNumber) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(pageNumber));
    const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error(`PLP infinite scroll HTTP ${response.status}`);
    const html = await response.text();
    return html;
  }

  appendItems(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const nextGrid = doc.querySelector(`[data-section-id="${this.sectionId}"][data-lazy-grid="true"]`) ||
      doc.querySelector(PLP_GRID_SELECTOR);
    if (!nextGrid) {
      console.warn('PLP: No grid found in fetched HTML');
      return 0;
    }
    let items = Array.from(nextGrid.querySelectorAll('[data-product-grid-item]'));
    if (!items.length) {
      items = Array.from(nextGrid.querySelectorAll('li:not([data-plp-skeleton])'));
    }
    if (!items.length) {
      console.warn('PLP: No product items found in fetched HTML');
      return 0;
    }

    const skeletonPlaceholders = Array.from(this.grid.querySelectorAll('[data-lazy-placeholder="true"]'));

    if (skeletonPlaceholders.length > 0) {
      items.forEach((item) => {
        item.removeAttribute('aria-hidden');
        item.removeAttribute('role');
        const firstPlaceholder = this.grid.querySelector('[data-lazy-placeholder="true"]');
        if (firstPlaceholder) {
          firstPlaceholder.replaceWith(item);
        } else {
          this.grid.appendChild(item);
        }
      });
    } else {
      const fragment = document.createDocumentFragment();
      items.forEach((item) => {
        item.removeAttribute('aria-hidden');
        item.removeAttribute('role');
        fragment.appendChild(item);
      });
      this.grid.appendChild(fragment);
    }
    return items.length;
  }

  emitAppendEvent(count, pageNumber) {
    window.dispatchEvent(
      new CustomEvent('plpLazyBatchLoaded', {
        detail: { page: pageNumber, count, sectionId: this.sectionId },
      })
    );
    window.dispatchEvent(
      new CustomEvent('facets:update', {
        detail: { reason: 'plp-infinite-scroll', sectionId: this.sectionId },
      })
    );
  }

  injectSkeletons() {
    if (!this.skeletonTemplate?.content?.firstElementChild) return;
    const currentCount = this.grid.querySelectorAll('[data-lazy-placeholder="true"]').length;
    const desired = Math.max(1, this.skeletonCount);
    const needed = desired - currentCount;
    if (needed <= 0) return;
    const fragment = document.createDocumentFragment();
    this.skeletonNodes = [];
    for (let i = 0; i < needed; i += 1) {
      const clone = this.skeletonTemplate.content.firstElementChild.cloneNode(true);
      clone.dataset.plpSkeleton = 'true';
      clone.dataset.lazyPlaceholder = 'true';
      fragment.appendChild(clone);
      this.skeletonNodes.push(clone);
    }
    this.grid.appendChild(fragment);
  }

  removeSkeletons() {
    if (!Array.isArray(this.skeletonNodes)) return;
    this.skeletonNodes.forEach((node) => {
      node.style.opacity = '0';
      node.style.transition = 'opacity 0.15s ease-out';
      setTimeout(() => node.remove(), 150);
    });
    this.skeletonNodes = [];
  }

  setSentinelState(state) {
    if (!this.sentinel) return;
    this.sentinel.classList.toggle('is-loading', state === 'loading');
    this.sentinel.classList.toggle('has-error', state === 'error');
    this.sentinel.classList.toggle('is-complete', state === 'complete');
    const indicator = this.sentinel.querySelector('.plp-lazy-indicator');
    indicator?.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
      const message = this.sentinel.querySelector('[data-plp-sentinel-message]');
      if (message) {
        if (state === 'error') {
          message.textContent = this.messages.error;
        } else if (state === 'complete') {
          message.textContent = this.messages.complete;
        } else {
          message.textContent = this.messages.loading;
        }
      }
      const retryButton = this.sentinel.querySelector('[data-plp-retry]');
      if (retryButton) {
        retryButton.hidden = state !== 'error';
        retryButton.disabled = state === 'loading';
        retryButton.textContent = this.messages.retry;
        if (!retryButton.dataset.boundRetry) {
          retryButton.addEventListener('click', () => {
            if (this.pendingPage) {
            this.requestPage(this.pendingPage);
          } else {
            this.loadNextPage();
          }
        });
        retryButton.dataset.boundRetry = 'true';
      }
    }
  }

  complete() {
    this.setSentinelState('complete');

    const remainingSkeletons = this.grid.querySelectorAll('[data-lazy-placeholder="true"]');
    remainingSkeletons.forEach(skeleton => skeleton.remove());

    if (this.observer && this.sentinel) {
      this.observer.unobserve(this.sentinel);
    }
    if (this.paginationFallback) {
      this.paginationFallback.hidden = true;
    }
  }

  teardownSentinel() {
    if (this.paginationFallback) {
      this.paginationFallback.hidden = false;
    }
    if (this.sentinel) {
      this.sentinel.hidden = true;
    }
  }

  disconnect() {
    this.observer?.disconnect();
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
  }

  maybePrimeInitialBatch() {
    if (!this.sentinel || this.loading) return;
    const rect = this.sentinel.getBoundingClientRect();
    const viewport = window.innerHeight || 0;
    if (rect.top <= viewport + 120) {
      this.loadNextPage();
    }
  }
}

const readInlineConfig = (sectionId) => {
  if (!sectionId) return null;
  const script = document.getElementById(`ShopCollectionLoaderConfig-${sectionId}`);
  if (!script) return null;
  try {
    return JSON.parse(script.textContent);
  } catch (error) {
    console.warn('Unable to parse ShopCollectionLoaderConfig', error);
    return null;
  }
};

export function initShopAllBatchLoader(options = {}) {
  ready(() => {
    const loader = new SectionReplaceLoader(options);
    loader.init();
  });
}

export function initCollectionGridLoader(overrides = {}) {
  const resolve = () => {
    const inlineConfig = readInlineConfig(overrides.sectionId || window.ShopCollectionLoaderConfig?.sectionId);
    const baseline = inlineConfig || window.ShopCollectionLoaderConfig || {};
    const merged = Object.assign({}, baseline, overrides);
    if (!merged.sectionId) return null;
    window.ShopCollectionLoaderConfig = merged;
    if (merged.lazyEnabled === false) {
      if (currentGridLoader) currentGridLoader.disconnect();
      currentGridLoader = null;
      return null;
    }
    if (currentGridLoader) currentGridLoader.disconnect();
    currentGridLoader = new CollectionInfiniteScroll(merged);
    currentGridLoader.init();
    return currentGridLoader;
  };

  ready(resolve);
  return currentGridLoader;
}

const autoInitCollectionLoader = () => {
  if (window.Shopify?.designMode) return;
  const inlineScripts = document.querySelectorAll('[id^="ShopCollectionLoaderConfig-"]');
  if (!inlineScripts.length && !window.ShopCollectionLoaderConfig) return;
  const sectionId = window.ShopCollectionLoaderConfig?.sectionId || inlineScripts[0]?.id?.replace('ShopCollectionLoaderConfig-', '');
  if (!sectionId) return;
  if (!window.ShopCollectionLoaderConfig) {
    const inlineConfig = readInlineConfig(sectionId);
    if (inlineConfig) window.ShopCollectionLoaderConfig = inlineConfig;
  }
  if (window.ShopCollectionLoaderConfig?.lazyEnabled === false) return;
  initCollectionGridLoader({ sectionId });
};

if (typeof window !== 'undefined') {
  window.HuhaCollectionLoader = {
    refresh: (overrides = {}) => {
      const sectionId = overrides.sectionId || window.ShopCollectionLoaderConfig?.sectionId;
      const inlineConfig = readInlineConfig(sectionId);
      const merged = Object.assign({}, inlineConfig || {}, overrides);
      if (!merged.sectionId) return;
      window.ShopCollectionLoaderConfig = merged;
      initCollectionGridLoader(merged);
    },
  };

  ready(autoInitCollectionLoader);
  document.addEventListener('shopify:section:load', autoInitCollectionLoader);
}
